import { STORAGE_CLEANUP_PATTERNS } from "./data";
import { FEATURE_NAMES, logInfo } from "@/loggers";

export interface StorageCleanupResult {
  clearedKeys: string[];
  errors: Error[];
}

/**
 * Clears authentication-related items from localStorage based on patterns
 * @returns Result object with cleared keys and any errors
 */
export const clearAuthStorage = (): StorageCleanupResult => {
  const keysToRemove: string[] = [];
  const errors: Error[] = [];

  try {
    // Collect keys that match our patterns
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && STORAGE_CLEANUP_PATTERNS.some((pattern) => key.includes(pattern))) {
        keysToRemove.push(key);
      }
    }

    // Remove collected keys
    keysToRemove.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        errors.push(new Error(`Failed to remove key ${key}: ${error}`));
      }
    });

    if (keysToRemove.length > 0) {
      logInfo(FEATURE_NAMES.MVS, `Cleared ${keysToRemove.length} authentication-related localStorage items`);
    }
  } catch (error) {
    errors.push(new Error(`Failed to iterate localStorage: ${error}`));
  }

  return { clearedKeys: keysToRemove, errors };
};

/**
 * Clears all browser storage (localStorage and sessionStorage)
 */
export const clearAllStorage = (): void => {
  try {
    localStorage.clear();
    sessionStorage.clear();
    logInfo(FEATURE_NAMES.MVS, "Cleared all browser storage");
  } catch (error) {
    // Log but don't throw - we want logout to continue even if storage clearing fails
    logInfo(FEATURE_NAMES.MVS, `Error clearing browser storage: ${error}`);
  }
};

/**
 * Removes a specific item from localStorage with error handling
 */
export const removeStorageItem = (key: string): boolean => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    logInfo(FEATURE_NAMES.MVS, `Failed to remove storage item ${key}: ${error}`);
    return false;
  }
};
